'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOptions {
  species: string[];
  breeds: string[];
  ages: string[];
  sizes: string[];
  temperaments: string[];
  specialNeeds: string[];
  locations: string[];
}

interface FilterPanelProps {
  onFilterChange: (filters: FilterState) => void;
  onSaveSearch: () => void;
  activeFilters: FilterState;
  canSave: boolean;
}

export interface FilterState {
  species: string;
  breed: string;
  age: string;
  size: string;
  temperament: string;
  specialNeeds: string;
  location: string;
  searchQuery: string;
}

const FilterPanel = ({ onFilterChange, onSaveSearch, activeFilters, canSave }: FilterPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [localFilters, setLocalFilters] = useState<FilterState>(activeFilters);

  const filterOptions: FilterOptions = {
    species: ['All Species', 'Dog', 'Cat', 'Bird', 'Rabbit', 'Reptile', 'Small Mammal', 'Fish'],
    breeds: ['All Breeds', 'Labrador Retriever', 'Persian Cat', 'Parakeet', 'Holland Lop', 'Bearded Dragon', 'Guinea Pig', 'Goldfish'],
    ages: ['All Ages', 'Puppy/Kitten (0-1 year)', 'Young (1-3 years)', 'Adult (3-7 years)', 'Senior (7+ years)'],
    sizes: ['All Sizes', 'Small (0-20 lbs)', 'Medium (20-50 lbs)', 'Large (50-100 lbs)', 'Extra Large (100+ lbs)'],
    temperaments: ['All Temperaments', 'Friendly', 'Energetic', 'Calm', 'Playful', 'Independent', 'Affectionate', 'Protective'],
    specialNeeds: ['All', 'Special Medical Needs', 'Behavioral Training Required', 'Senior Care', 'Disability Accommodations'],
    locations: ['All Locations', 'Within 10 miles', 'Within 25 miles', 'Within 50 miles', 'Within 100 miles', 'Statewide']
  };

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    const updatedFilters = { ...localFilters, [key]: value };
    setLocalFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedFilters = { ...localFilters, searchQuery: e.target.value };
    setLocalFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters: FilterState = {
      species: 'All Species',
      breed: 'All Breeds',
      age: 'All Ages',
      size: 'All Sizes',
      temperament: 'All Temperaments',
      specialNeeds: 'All',
      location: 'All Locations',
      searchQuery: ''
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const activeFilterCount = Object.entries(localFilters).filter(([key, value]) => {
    if (key === 'searchQuery') return value !== '';
    return !value.toLowerCase().includes('all');
  }).length;

  return (
    <div className="bg-card rounded-lg shadow-md border border-border">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="AdjustmentsHorizontalIcon" size={24} variant="outline" className="text-primary" />
            <h2 className="text-xl font-headline font-semibold text-foreground">Filter Your Search</h2>
            {activeFilterCount > 0 && (
              <span className="px-2 py-1 bg-brand-cta text-white text-xs font-semibold rounded-full">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-muted rounded-lg transition-colors lg:hidden"
            aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
          >
            <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={20} variant="outline" />
          </button>
        </div>

        <div className="relative">
          <Icon name="MagnifyingGlassIcon" size={20} variant="outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, breed, or characteristics..."
            value={localFilters.searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground"
          />
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Species</label>
            <select
              value={localFilters.species}
              onChange={(e) => handleFilterChange('species', e.target.value)}
              className="w-full px-4 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              {filterOptions.species.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Breed</label>
            <select
              value={localFilters.breed}
              onChange={(e) => handleFilterChange('breed', e.target.value)}
              className="w-full px-4 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              {filterOptions.breeds.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Age Range</label>
            <select
              value={localFilters.age}
              onChange={(e) => handleFilterChange('age', e.target.value)}
              className="w-full px-4 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              {filterOptions.ages.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Size</label>
            <select
              value={localFilters.size}
              onChange={(e) => handleFilterChange('size', e.target.value)}
              className="w-full px-4 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              {filterOptions.sizes.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Temperament</label>
            <select
              value={localFilters.temperament}
              onChange={(e) => handleFilterChange('temperament', e.target.value)}
              className="w-full px-4 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              {filterOptions.temperaments.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Special Needs</label>
            <select
              value={localFilters.specialNeeds}
              onChange={(e) => handleFilterChange('specialNeeds', e.target.value)}
              className="w-full px-4 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              {filterOptions.specialNeeds.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Location</label>
            <select
              value={localFilters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              className="w-full px-4 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              {filterOptions.locations.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {activeFilterCount > 0 && (
            <div className="flex gap-2">
              <button
                onClick={onSaveSearch}
                disabled={!canSave}
                className="flex-1 px-4 py-2.5 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="BookmarkIcon" size={20} variant="outline" />
                <span>Save Search</span>
              </button>
              <button
                onClick={clearAllFilters}
                className="flex-1 px-4 py-2.5 bg-muted text-foreground font-semibold rounded-lg hover:bg-muted/80 transition-colors flex items-center justify-center space-x-2"
              >
                <Icon name="XMarkIcon" size={20} variant="outline" />
                <span>Clear</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterPanel;